import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/Admin/AdminLayout';
import { fetchWithSecurity, sanitizePayload } from '../../utils/security';
import { toast } from 'react-toastify';
import { RefreshCw, Trash2, Mail, Phone, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'https://orbitronix.ge/api/';

const AdminMessages = () => {
    const { user, isSuperAdmin, loading: authLoading } = useAuth();
    const { t } = useTranslation();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchMessages = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchWithSecurity(`${API_URL}contacts.php`);
            const data = await res.json();
            if (Array.isArray(data)) setMessages(data);
        } catch {
            toast.error(t('admin.messages.toastLoadError') || 'შეტყობინებების ჩატვირთვა ვერ მოხერხდა');
        }
        setLoading(false);
    }, [t]);

    useEffect(() => {
        if (user && isSuperAdmin()) fetchMessages();
    }, [user, isSuperAdmin, fetchMessages]);

    const handleStatusChange = async (id, newStatus) => {
        try {
            const sanitized = sanitizePayload({ status: newStatus });
            const res = await fetchWithSecurity(`${API_URL}contacts.php`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id, ...sanitized })
            });
            const data = await res.json();
            if (data.success) {
                toast.success(t('admin.messages.toastStatusUpdated') || 'სტატუსი განახლდა');
                setMessages(prev => prev.map(m => m.id === id ? { ...m, status: newStatus } : m));
            } else {
                toast.error(data.error || (t('admin.messages.toastStatusError') || 'სტატუსის განახლება ვერ მოხერხდა'));
            }
        } catch {
            toast.error(t('admin.messages.toastStatusError') || 'სტატუსის განახლება ვერ მოხერხდა');
        }
    };

    const deleteMessage = async (id) => {
        if (!window.confirm(t('admin.messages.deleteConfirm') || 'დარწმუნებული ხართ, რომ გსურთ ამ შეტყობინების წაშლა?')) return;
        try {
            const res = await fetchWithSecurity(`${API_URL}contacts.php?id=${id}`, { method: 'DELETE' });
            const data = await res.json();
            if (data.success) {
                toast.success(t('admin.messages.toastDeleted') || 'შეტყობინება წაიშალა');
                setMessages(prev => prev.filter(m => m.id !== id));
            } else {
                toast.error(data.error || (t('admin.messages.toastDeleteError') || 'წაშლა ვერ მოხერხდა'));
            }
        } catch {
            toast.error(t('admin.messages.toastDeleteError') || 'წაშლა ვერ მოხერხდა');
        }
    };

    if (authLoading || !user) return null;

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'New': return 'shadcn-badge-danger';
            case 'Read': return 'shadcn-badge-info';
            case 'Replied': return 'shadcn-badge-success';
            case 'Archived': return 'shadcn-badge-secondary';
            default: return 'shadcn-badge-secondary';
        }
    };

    return (
        <AdminLayout title={t('admin.messages.title') || 'კონტაქტი'}>
            <div className="shadcn-card">
                <div className="shadcn-card-header d-flex justify-content-between align-items-center">
                    <div className="shadcn-card-title">
                        <Mail size={20} className="text-indigo" />
                        <span>{t('admin.messages.title') || 'კონტაქტი'} ({messages.length})</span>
                    </div>
                    <button className="shadcn-btn shadcn-btn-secondary shadcn-btn-sm" onClick={fetchMessages}>
                        <RefreshCw size={14} /> {t('admin.messages.refresh') || 'განახლება'}
                    </button>
                </div>

                {loading ? (
                    <div className="text-center py-5 text-muted">
                        <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                        <span>{t('admin.messages.loading') || 'შეტყობინებები იტვირთება...'}</span>
                    </div>
                ) : messages.length === 0 ? (
                    <p className="text-center text-muted py-5 mb-0">{t('admin.messages.noMessages') || 'შეტყობინებები ჯერ არ არის მიღებული.'}</p>
                ) : (
                    <div className="shadcn-table-wrapper">
                        <table className="shadcn-table">
                            <thead>
                                <tr>
                                    <th>{t('admin.messages.colDate') || 'თარიღი & დრო'}</th>
                                    <th>{t('admin.messages.colSender') || 'გამომგზავნი'}</th>
                                    <th>{t('admin.messages.colContact') || 'საკონტაქტო ინფო'}</th>
                                    <th>{t('admin.messages.colMessage') || 'შეტყობინების ტექსტი'}</th>
                                    <th>{t('admin.messages.colStatus') || 'სტატუსი'}</th>
                                    <th>{t('admin.messages.colActions') || 'მოქმედებები'}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {messages.map(msg => (
                                    <tr key={msg.id}>
                                        <td>
                                            <div className="fw-semibold text-dark d-flex align-items-center gap-1">
                                                <Calendar size={13} className="text-muted" />
                                                <span>{new Date(msg.created_at).toLocaleDateString()}</span>
                                            </div>
                                            <div className="smallest text-muted">{new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                        </td>
                                        <td className="fw-bold text-dark">{msg.name}</td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1 text-dark small mb-1">
                                                <Mail size={13} className="text-primary" />
                                                <a href={`mailto:${msg.email}`} className="text-dark text-decoration-none">{msg.email}</a>
                                            </div>
                                            {msg.phone && (
                                                <div className="d-flex align-items-center gap-1 text-muted smallest">
                                                    <Phone size={11} />
                                                    <span>{msg.phone}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div className="text-dark small" style={{ maxWidth: '340px', maxHeight: '90px', overflowY: 'auto' }}>
                                                {msg.message}
                                            </div>
                                        </td>
                                        <td>
                                            <select
                                                className={`shadcn-select form-select-sm py-1 ${getStatusBadgeClass(msg.status)}`}
                                                value={msg.status}
                                                onChange={(e) => handleStatusChange(msg.id, e.target.value)}
                                                style={{ maxWidth: '140px' }}
                                            >
                                                <option value="New">{t('admin.messages.statusNew') || 'ახალი'}</option>
                                                <option value="Read">{t('admin.messages.statusRead') || 'წაკითხული'}</option>
                                                <option value="Replied">{t('admin.messages.statusReplied') || 'პასუხგაცემული'}</option>
                                                <option value="Archived">{t('admin.messages.statusArchived') || 'დაარქივებული'}</option>
                                            </select>
                                        </td>
                                        <td>
                                            <button className="shadcn-btn shadcn-btn-sm shadcn-btn-danger" onClick={() => deleteMessage(msg.id)}>
                                                <Trash2 size={14} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default AdminMessages;
